import firstValue from "./first.value";
const RECEIPT_GROUPS = ["receipt", "official_receipt", "payment_receipt", "receipts"];


const toLink = (value) => {
  const link = typeof value === "string" ? value : value?.url || value?.api_url || value?.file_url;
  if (typeof link !== "string") return null;
  try {
    const url = new URL(link, window.location.origin);
    return ["http:", "https:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

const fileName = (value, fallback) =>
  firstValue(value?.display_name, value?.file_name, value?.name, fallback);

const isReceipt = (file) =>
  RECEIPT_GROUPS.includes(file?.collection_type) || RECEIPT_GROUPS.includes(file?.image_type);

/**
 * The receipts the API returns live in `documents.official_receipt`, where each
 * file carries the `payment_slip_id` of the payment it belongs to. Several
 * files can share one payment, so they are grouped back into a payment each.
 */
function getReceiptDocuments(source) {
  const documents = source.documents;
  const fromDocuments = Array.isArray(documents)
    ? documents.filter(isReceipt)
    : RECEIPT_GROUPS.flatMap((group) => {
        const value = documents?.[group];
        return Array.isArray(value) ? value : [];
      });

  return [
    ...fromDocuments,
    ...RECEIPT_GROUPS.flatMap((group) => {
      const value = source[group];
      return Array.isArray(value) ? value : [];
    }),
    ...(Array.isArray(source.media) ? source.media : []).filter(isReceipt),
    ...(Array.isArray(source.attachments) ? source.attachments : []).filter(isReceipt),
  ].filter((file) => toLink(file));
}

function groupReceiptsByPayment(entries) {
  const payments = new Map();

  entries.forEach((entry, index) => {
    const key = firstValue(entry.payment_slip_id, entry.payment_id, index + 1);

    if (!payments.has(key)) {
      payments.set(key, {
        id: `receipt-${key}`,
        label: `Payment #${payments.size + 1}`,
        amount: firstValue(entry.amount, entry.paid_amount, null),
        date: firstValue(entry.paid_at, entry.created_at, null),
        status: firstValue(entry.status, null),
        files: [],
      });
    }

    const payment = payments.get(key);
    payment.files.push({
      url: toLink(entry),
      name: fileName(entry, `Official Receipt #${payment.files.length + 1}`),
    });
  });

  return Array.from(payments.values());
}

/**
 * The order payload has no single agreed shape for receipts, so accept the
 * spellings the API has used so far: receipt files inside `documents`, a
 * `payments` (or `instalments`) array where each entry carries its own
 * receipt(s), or a flat `receipts` array.
 */
export function getOrderReceipts(order) {
  const source = order?.source || order || {};

  const receiptDocuments = getReceiptDocuments(source);
  if (receiptDocuments.length) return groupReceiptsByPayment(receiptDocuments);

  const payments = [
    source.payments,
    source.payment_records,
    source.instalments,
    source.installments,
  ].find((value) => Array.isArray(value) && value.length);

  if (!payments) return [];

  return payments
    .map((payment, index) => {
      const files = [
        payment.receipts,
        payment.official_receipts,
        payment.receipt,
        payment.official_receipt,
        payment.document,
      ].find((value) => value !== undefined && value !== null);

      const entries = (Array.isArray(files) ? files : [files]).filter(Boolean);

      return {
        id: firstValue(payment.id, payment.reference_no, index + 1),
        label: firstValue(payment.label, payment.title, `Payment #${index + 1}`),
        amount: firstValue(payment.amount, payment.paid_amount, payment.total, null),
        date: firstValue(payment.paid_at, payment.date, payment.created_at, null),
        status: firstValue(payment.status, payment.payment_status, null),
        files: entries
          .map((entry, fileIndex) => ({
            url: toLink(entry),
            name: fileName(entry, `Official Receipt #${fileIndex + 1}`),
          }))
          .filter((file) => file.url),
      };
    })
    .filter((payment) => payment.files.length);
}

export function hasOrderReceipts(order) {
  return getOrderReceipts(order).some((payment) => payment.files.length);
}
